import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  CarOutlined, 
  ClockCircleOutlined, 
  EnvironmentOutlined, 
  CalendarOutlined,
  LeftOutlined,
  RightOutlined,
  DownOutlined,
  CheckOutlined,
  LoadingOutlined
} from '@ant-design/icons';
import { message } from 'antd';
import axios from '../../../../../utils/axiosConfig';
import dayjs from 'dayjs';
import { MapContainer, TileLayer, CircleMarker, Circle, Tooltip } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix for default marker icons in react-leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const SelectStops = ({ campaignData, onUpdate, onNext, isAdminCreating, targetUserId }) => {
  const [stops, setStops] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true); // Start with loading true
  const [geotabDatabases, setGeotabDatabases] = useState([]);
  const stopsPerPage = 6;
  const fetchAbortRef = useRef(null);

  const authHeader = () => {
    const token = sessionStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchGeotabDatabases = useCallback(async () => {
    try {
      // If admin is creating for another user, fetch that user's Geotab accounts
      // Otherwise, fetch for the current logged-in user (no userId param needed)
      const endpoint = (isAdminCreating === true && targetUserId) 
        ? `/api/integrations/geotab/accounts?userId=${targetUserId}`
        : '/api/integrations/geotab/accounts';
      
      console.log('SelectStops - Fetching Geotab accounts from:', endpoint);
      const { data } = await axios.get(endpoint, { headers: authHeader() });
      const items = Array.isArray(data) ? data : (Array.isArray(data?.items) ? data.items : []);
      const databases = items
        .map(a => a?.database)
        .filter(Boolean)
        .filter((v, i, arr) => arr.indexOf(v) === i);
      
      console.log('Found databases:', databases);
      if (databases.length > 0) {
        console.log('Setting geotabDatabases to:', databases);
        setGeotabDatabases(databases);
      } else {
        console.log('No databases found');
        message.warning('No Geotab databases found. Please configure Geotab integration.');
        setGeotabDatabases([]);
      }
    } catch (e) {
      console.error('Error fetching Geotab accounts:', e);
      message.error('Failed to connect to Geotab. Please check your integration settings.');
      setGeotabDatabases([]);
    }
  }, [isAdminCreating, targetUserId]);

  const fetchGeotabStops = useCallback(async () => {
    if (geotabDatabases.length === 0) {
      setStops([]);
      return;
    }

    // Set loading immediately
    setLoading(true);
    setCurrentPage(1);

    // Use provided date range or default to last 7 days
    let startDate, endDate;
    if (campaignData.dateRange && campaignData.dateRange.length === 2 && campaignData.dateRange[0] && campaignData.dateRange[1]) {
      startDate = campaignData.dateRange[0].startOf('day');
      endDate = campaignData.dateRange[1].endOf('day');
    } else {
      // Default to last 7 days if no date range set
      startDate = dayjs().subtract(7, 'day').startOf('day');
      endDate = dayjs().endOf('day');
    }

    try {
      if (fetchAbortRef.current) {
        fetchAbortRef.current.abort();
      }
      const abortController = new AbortController();
      fetchAbortRef.current = abortController;
      const query = new URLSearchParams();
      query.set('limit', '500');
      query.set('maxAddresses', '500');
      
      if (geotabDatabases.length > 0) {
        query.set('databases', geotabDatabases.join(','));
      } else {
        query.set('all', 'true');
      }
      
      query.set('startDate', startDate.toISOString());
      query.set('endDate', endDate.toISOString());

      // Add stop duration filters from campaignData
      const stopDuration = campaignData.stopDuration || { min: 15, max: 720 };
      query.set('minStopMinutes', stopDuration.min.toString());
      query.set('maxStopMinutes', stopDuration.max.toString());

      // Pass selected vehicle IDs so backend only queries those devices
      const selectedVehicleIds = campaignData.selectedVehicles || [];
      if (selectedVehicleIds.length > 0) {
        query.set('deviceIds', selectedVehicleIds.join(','));
      }

      console.log('=== SelectStops API Call ===');
      console.log('Date Range:', startDate.format('YYYY-MM-DD'), 'to', endDate.format('YYYY-MM-DD'));
      console.log('Stop Duration:', `${stopDuration.min} - ${stopDuration.max} minutes`);
      console.log('Databases:', geotabDatabases);
      console.log('Full Query:', query.toString());

      console.log('Making API request to:', `/api/integrations/geotab/gps-addresses?${query.toString()}`);
      console.log('Auth header:', authHeader());
      
      const response = await axios.get(`/api/integrations/geotab/gps-addresses?${query.toString()}`, {
        headers: authHeader(),
        signal: abortController.signal,
      });

      console.log('=== API RESPONSE RECEIVED ===');
      console.log('Response status:', response.status);
      console.log('Response data:', response.data);
      console.log('Response data type:', typeof response.data);
      console.log('Has addresses property?', response.data?.addresses !== undefined);
      console.log('Addresses count:', response.data?.addresses?.length || 0);
      
      if (response.data?.addresses && Array.isArray(response.data.addresses)) {
        console.log('Processing', response.data.addresses.length, 'addresses');
        let formattedStops = response.data.addresses.map((addr, index) => {
          const stopStart = addr.firstSeen ? dayjs(addr.firstSeen) : (addr.dateTime ? dayjs(addr.dateTime) : null);
          const deviceId = addr.deviceId?.id || addr.deviceId || addr.device?.id || null;
          const durationFromApi = Number(addr.totalDurationMinutes ?? addr.totalDurationMin ?? addr.stopDurationMinutes ?? 0);
          const durationMinutes = Number.isFinite(durationFromApi) ? durationFromApi : 0;

          const lat = addr.latitude || 0;
          const lng = addr.longitude || 0;
          const fallbackAddress = addr.address || `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
          
          return {
            id: addr.id || `geotab_${index}`,
            deviceId: deviceId ? String(deviceId) : null,
            vehicleName: addr.device || addr.database || 'Unknown Vehicle',
            vehicleColor: getVehicleColor(index),
            date: stopStart ? stopStart.format('MMM D, YYYY') : 'Unknown Date',
            time: stopStart ? stopStart.format('h:mm A') : 'Unknown Time',
            address: fallbackAddress,
            duration: durationMinutes,
            coords: {
              lat: lat,
              lng: lng
            },
            city: addr.city,
            state: addr.state,
            postcode: addr.postcode,
            database: addr.database
          };
        });

        console.log('Formatted stops:', formattedStops.length);
        console.log('First stop sample:', formattedStops[0]);
        
        setStops(formattedStops);
        console.log('Stops state set with', formattedStops.length, 'stops');
        
        setCurrentPage(1);
        
        if (formattedStops.length > 0) {
          message.success(`Loaded ${formattedStops.length} stops from Geotab`);
        } else {
          message.info('No stops found in the selected date range.');
        }
      } else {
        console.error('No addresses in response or invalid format');
        console.error('Response data:', response.data);
        setStops([]);
        message.warning('No GPS data returned from Geotab.');
      }
    } catch (error) {
      if (error?.name === 'CanceledError' || error?.name === 'AbortError') {
        console.log('Request was aborted');
        return;
      }
      
      console.error('=== ERROR FETCHING STOPS ===');
      console.error('Error:', error);
      console.error('Error message:', error?.message);
      console.error('Error response:', error?.response);
      console.error('Error response data:', error?.response?.data);
      console.error('Error stack:', error?.stack);
      
      if (error?.code === 'ECONNABORTED' || error?.message?.includes('timeout')) {
        message.error('Request timed out. Try reducing the date range.');
      } else if (error?.response?.status === 401) {
        message.error('Authentication failed. Please log in again.');
      } else if (error?.response?.status === 500) {
        message.error('Server error. Please try again.');
      } else {
        message.error(error?.response?.data?.error || error?.message || 'Failed to fetch stops from Geotab.');
      }
      setStops([]);
    } finally {
      console.log('Finally block - setting loading to false');
      setLoading(false);
    }
  }, [campaignData.dateRange, campaignData.stopDuration, campaignData.selectedVehicles, geotabDatabases]);

  useEffect(() => {
    fetchGeotabDatabases();
  }, [fetchGeotabDatabases]);

  useEffect(() => {
    console.log('=== geotabDatabases changed ===');
    console.log('geotabDatabases:', geotabDatabases);
    console.log('geotabDatabases.length:', geotabDatabases.length);
    
    if (geotabDatabases.length > 0) {
      console.log('Calling fetchGeotabStops...');
      fetchGeotabStops();
    } else if (geotabDatabases.length === 0) {
      // No databases found, clear loading state
      console.log('No databases, clearing loading state');
      setLoading(false);
      setStops([]);
    }
  }, [fetchGeotabStops, geotabDatabases]);

  // Debug: Track loading state
  useEffect(() => {
    console.log('Loading state changed:', loading);
  }, [loading]);

  // Debug: Track stops state
  useEffect(() => {
    console.log('Stops state changed:', stops.length, 'stops');
  }, [stops]);

  // Define helper functions BEFORE the loading check
  const getVehicleColor = (index) => {
    const colors = ['var(--ink)', '#ef4444', '#22c55e', '#f59e0b', '#8b5cf6', '#ec4899'];
    return colors[index % colors.length];
  };

  // Log at the very start of render
  console.log('=== SelectStops RENDER ===');
  console.log('Loading:', loading);
  console.log('Stops:', stops.length);
  console.log('GeotabDatabases:', geotabDatabases);

  // Show loading state FIRST - before any calculations
  if (loading) {
    console.log('=== SHOWING LOADING SPINNER (SelectStops) ===');
    return (
      <div className="step-container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <div style={{ textAlign: 'center' }}>
          <LoadingOutlined style={{ fontSize: 48, color: 'var(--ink)', marginBottom: 16 }} spin />
          <p style={{ color: 'var(--ink)', fontSize: 16, fontWeight: 500, marginBottom: 8 }}>Loading stops from Geotab...</p>
          <p style={{ color: '#8c8c8c', fontSize: 14 }}>Fetching GPS addresses for stops</p>
        </div>
      </div>
    );
  }

  const totalPages = Math.ceil(stops.length / stopsPerPage);
  const startIndex = (currentPage - 1) * stopsPerPage;
  const visibleStops = stops.slice(startIndex, startIndex + stopsPerPage);

  const selectedStops = campaignData.selectedStops || [];
  const targetingRadius = campaignData.targetingRadius || 0.5;

  const toggleStop = (stopId) => {
    const isSelected = selectedStops.includes(stopId);
    if (isSelected) {
      const newSelectedStops = selectedStops.filter(id => id !== stopId);
      const selectedStopsData = stops.filter(s => newSelectedStops.includes(s.id));
      onUpdate({ 
        selectedStops: newSelectedStops,
        selectedStopsData: selectedStopsData
      });
    } else {
      const newSelectedStops = [...selectedStops, stopId];
      const selectedStopsData = stops.filter(s => newSelectedStops.includes(s.id));
      onUpdate({ 
        selectedStops: newSelectedStops,
        selectedStopsData: selectedStopsData
      });
    }
  };

  const selectAll = () => {
    const allIds = stops.map(s => s.id);
    onUpdate({ 
      selectedStops: allIds,
      selectedStopsData: stops
    });
  };

  const unselectAll = () => {
    onUpdate({ 
      selectedStops: [],
      selectedStopsData: []
    });
  };

  const handleContinue = () => {
    if (selectedStops.length === 0) {
      message.warning('Please select at least one stop');
      return;
    }
    // Ensure selectedStopsData is up to date before continuing
    const selectedStopsData = stops.filter(s => selectedStops.includes(s.id));
    onUpdate({ selectedStopsData: selectedStopsData });
    onNext();
  };

  const formatDuration = (minutes) => {
    const roundedMinutes = Math.round(minutes);
    if (roundedMinutes >= 60) {
      const hours = Math.floor(roundedMinutes / 60);
      const mins = roundedMinutes % 60;
      return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
    }
    return `${roundedMinutes}m`;
  };

  return (
    <div className="step-container">
      <h1 className="step-title">Select Stops</h1>
      <p className="step-subtitle">Choose the job stops to include in your campaign</p>

      <div className="stops-container">
        {/* Header */}
        <div className="stops-header">
          <div className="stops-header-left">
            <button className="select-all-btn" onClick={selectAll} disabled={stops.length === 0}>
              Select All
            </button>
            <button className="select-all-btn" onClick={unselectAll} disabled={selectedStops.length === 0} style={{ marginLeft: '8px' }}>
              Unselect All
            </button>
          </div>
          <div className="stops-header-right">
            {selectedStops.length} of {stops.length} stops selected • {targetingRadius} mi radius
          </div>
        </div>

        {/* Stops Grid */}
        {stops.length > 0 && (
          <div className="stops-grid">
            {visibleStops.map((stop) => {
            const isSelected = selectedStops.includes(stop.id);
            return (
              <div
                key={stop.id}
                className={`stop-card ${isSelected ? 'selected' : ''}`}
                onClick={() => toggleStop(stop.id)}
              >
                <div className="stop-card-header">
                  <span 
                    className="stop-color-dot" 
                    style={{ backgroundColor: stop.vehicleColor }}
                  />
                  <CarOutlined style={{ color: 'var(--ink-200)', fontSize: 14 }} />
                  <span className="stop-vehicle-name">{stop.vehicleName}</span>
                  {isSelected && (
                    <div style={{ 
                      marginLeft: 'auto', 
                      width: 20, 
                      height: 20, 
                      borderRadius: '50%', 
                      background: 'var(--ink)', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center' 
                    }}>
                      <CheckOutlined style={{ color: '#fff', fontSize: 11 }} />
                    </div>
                  )}
                </div>

                <div className="stop-card-details">
                  <div className="stop-detail-row">
                    <CalendarOutlined className="icon" />
                    <span>{stop.date} • {stop.time}</span>
                  </div>
                  <div className="stop-detail-row">
                    <EnvironmentOutlined className="icon" />
                    <span>{stop.address}</span>
                  </div>
                </div>

                <div className="stop-card-footer">
                  <div className="stop-duration">
                    <ClockCircleOutlined />
                    <span>{formatDuration(stop.duration)}</span>
                  </div>
                  <span className="stop-coords">
                    {stop.coords.lat.toFixed(4)}, {stop.coords.lng.toFixed(4)}
                  </span>
                </div>
              </div>
            );
          })}
          </div>
        )}

        {/* Pagination */}
        {!loading && totalPages > 1 && (
          <div className="pagination" style={{ marginTop: 24 }}>
            <button 
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              <LeftOutlined />
            </button>
            <span>Page {currentPage} of {totalPages}</span>
            <button 
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
            >
              <RightOutlined />
            </button>
          </div>
        )}
      </div>

      {/* Map - Same style as SelectRoutes */}
      {!loading && stops.length > 0 && (
        <div style={{ marginTop: 24, width: '100%' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: 'var(--ink)' }}>
              {selectedStops.length > 0 ? `Selected Stop Locations (${selectedStops.length})` : 'Stop Locations'}
            </h3>
            
            {/* Radius Selector Buttons */}
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <span style={{ fontSize: 13, color: 'var(--ink-200)', marginRight: 4 }}>Radius:</span>
              {[0.5, 1, 2, 3].map((radius) => (
                <button
                  key={radius}
                  onClick={() => onUpdate({ targetingRadius: radius })}
                  style={{
                    padding: '6px 12px',
                    background: targetingRadius === radius ? 'var(--ink)' : '#fff',
                    color: targetingRadius === radius ? '#fff' : 'var(--ink-100)',
                    border: targetingRadius === radius ? 'none' : '1px solid var(--paper-300)',
                    borderRadius: '6px',
                    fontSize: '13px',
                    fontWeight: 500,
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                  }}
                >
                  {radius} mi
                </button>
              ))}
            </div>
          </div>
          
          <div style={{ 
            height: '500px', 
            width: '100%',
            borderRadius: '12px', 
            overflow: 'hidden',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            border: '1px solid var(--paper-300)',
            position: 'relative',
            zIndex: 1
          }}>
            <MapContainer
              center={[35.5175, -86.5804]}
              zoom={8}
              style={{ height: '100%', width: '100%' }}
              scrollWheelZoom={true}
            >
              <TileLayer
                attribution='© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              
              {/* Display selected stop markers with radius bubbles */}
              {stops
                .filter(stop => selectedStops.includes(stop.id))
                .map((stop) => {
                  if (!stop.coords || !stop.coords.lat || !stop.coords.lng) return null;
                  
                  const position = [stop.coords.lat, stop.coords.lng];
                  // Convert miles to meters (1 mile = 1609.34 meters)
                  const radiusMeters = targetingRadius * 1609.34;
                  
                  return (
                    <React.Fragment key={`stop-${stop.id}`}>
                      {/* Radius bubble - light blue */}
                      <Circle
                        center={position}
                        radius={radiusMeters}
                        pathOptions={{
                          color: 'var(--ink)',
                          fillColor: 'var(--ink)',
                          fillOpacity: 0.15,
                          weight: 2,
                          opacity: 0.5
                        }}
                      />
                      <CircleMarker
                        center={position}
                        radius={14}
                        pathOptions={{ 
                          color: '#fff',
                          fillColor: stop.vehicleColor,
                          fillOpacity: 1,
                          weight: 4
                        }}
                      >
                      <Tooltip permanent={false} direction="top">
                        <div style={{ textAlign: 'left', minWidth: '220px' }}>
                          <div style={{ fontSize: '18px', marginBottom: '6px', textAlign: 'center' }}>🛑 Stop Location</div>
                          <strong style={{ color: stop.vehicleColor, fontSize: '14px' }}>{stop.vehicleName}</strong><br/>
                          <span style={{ fontSize: '12px', color: '#666' }}>{stop.date} • {stop.time}</span><br/>
                          <span style={{ fontSize: '13px', marginTop: '4px', display: 'block' }}>{stop.address}</span><br/>
                          <div style={{ marginTop: '6px', padding: '4px 8px', background: '#f0f0f0', borderRadius: '4px', display: 'inline-block' }}>
                            <ClockCircleOutlined style={{ marginRight: '4px' }} />
                            <strong>Duration:</strong> {formatDuration(stop.duration)}
                          </div><br/>
                          <span style={{ color: 'var(--sage)', fontWeight: 600, marginTop: '6px', display: 'block' }}>✓ Selected Stop</span>
                        </div>
                      </Tooltip>
                      </CircleMarker>
                    </React.Fragment>
                  );
                })}
            </MapContainer>
          </div>
          {selectedStops.length === 0 && (
            <p style={{ marginTop: 12, color: '#8c8c8c', fontSize: 14, textAlign: 'center' }}>
              Select stops above to see them on the map with their trip routes
            </p>
          )}
        </div>
      )}

      {/* Footer */}
      <div className="campaign-footer">
        <div className="footer-info">
          <strong>{selectedStops.length}</strong> stops selected • {targetingRadius} mi radius
        </div>
        <button 
          className="continue-btn" 
          onClick={handleContinue}
        >
          Continue
          <DownOutlined />
        </button>
      </div>
    </div>
  );
};

export default SelectStops;
